import Storage from "@/services/storage";
import { StorageConstants } from "@/enums/storage.enum";
import { LocationStatus, LocationsStatuses } from "@/modules/location/status";
import { LocationStatusType } from "@/types/locations/stutus";
import { LocationStatusInterface } from "@/interface/locations/status";
import Parse from "parse/dist/parse.min.js";
import { Back4AppInterface, BusinessInterface } from "@/interface/business/business";
import { UserInterface } from "@/interface/user.interface";
import { Business } from "@/modules/business/business";
import { BranchesMessagesType, BranchesStatusesType } from "@/types/business/business";

/**
 * Back For App Class
 */
class Back4App implements Back4AppInterface {
  locationList: Array<any> = [];
  channelsList: any = {};
  liveClient: any = null;
  liveChat: any = null;
  liveChatSubscribe: any = {};
  chartMessageQuery: any = null;
  locationsStatuses: any;
  business: BusinessInterface = new Business(null);
  liveClientSubscribe: any;
  locationIds?: Array<string> = [];
  key: number = 1;
  chatQuery: any = null;
  messages: Array<any> = [];
  branchesNotifications: any = {};
  branchesStatuses: BranchesStatusesType = {};
  branchesMessages: BranchesMessagesType = {};
  isBranchMessagesEmpty: boolean = false;
  locationLiveStatus: any = null;
  locationLiveNotifications: any = null;
  locationLiveNotificationsSubscribe: any = null;
  senderId: string = "";

  constructor() {
    // this.unSubscribe();

    Parse.initialize(
      process.env.VUE_APP_BACKFOURAPP_APPLICATION_ID,
      process.env.VUE_APP_BACKFOURAPP_JAVASCRIPT_KEY,
      process.env.VUE_APP_BACKFOURAPP_MASTER_KEY
    );

    Parse.serverURL = `${process.env.VUE_APP_BACKFOURAPP_SERVER_URI_HTTPS}`;

    if (!this.liveClient) {
      // init liveClient
      this.liveClient = new Parse.LiveQueryClient({
        masterKey: process.env.VUE_APP_BACKFOURAPP_MASTER_KEY,
        serverURL: process.env.VUE_APP_BACKFOURAPP_SERVER_URI_WSS,
        applicationId: process.env.VUE_APP_BACKFOURAPP_APPLICATION_ID,
        javascriptKey: process.env.VUE_APP_BACKFOURAPP_JAVASCRIPT_KEY,
      });
      this.liveClient.open();
    }

    if (!this.locationLiveStatus) {
      // init locationLiveStatus
      this.locationLiveStatus = new Parse.LiveQueryClient({
        masterKey: process.env.VUE_APP_BACKFOURAPP_MASTER_KEY,
        serverURL: process.env.VUE_APP_BACKFOURAPP_SERVER_URI_WSS,
        applicationId: process.env.VUE_APP_BACKFOURAPP_APPLICATION_ID,
        javascriptKey: process.env.VUE_APP_BACKFOURAPP_JAVASCRIPT_KEY,
      });
      this.locationLiveStatus.open();
    }

    if (!this.locationLiveNotifications) {
      // init locationLiveNotifications
      this.locationLiveNotifications = new Parse.LiveQueryClient({
        masterKey: process.env.VUE_APP_BACKFOURAPP_MASTER_KEY,
        serverURL: process.env.VUE_APP_BACKFOURAPP_SERVER_URI_WSS,
        applicationId: process.env.VUE_APP_BACKFOURAPP_APPLICATION_ID,
        javascriptKey: process.env.VUE_APP_BACKFOURAPP_JAVASCRIPT_KEY,
      });
      this.locationLiveNotifications.open();
    }

    this.business = Storage.get(StorageConstants.BUSINESS);
    this.locationIds = Storage.get(StorageConstants.LOCATIONS)?.flatMap((location) => location.id);
    this.senderId = `call_center_${this.business.id}`;

    (async () => {
      await this.getStatusLocations();
      await this.setLiveChannelsIdes();
      await this.setNotificationMessages();
    })();
  }

  /**
   * Update Specific location using addEventListener
   * @param locationStatus
   */
  async updateOneLocationStatus(locationStatus: LocationStatusType) {
    const updatedLocationStatus = new LocationStatus(locationStatus);
    const branchesStatuses = Object.assign({}, this.branchesStatuses);
    this.branchesStatuses = null;
    branchesStatuses[locationStatus.attributes.locationID] = locationStatus;
    this.branchesStatuses = Object.assign({}, branchesStatuses);

    return await this.locationsStatuses.updateLocationStatus(updatedLocationStatus);
  }

  /**
   * Init location statuses events
   * @return {Promise<any>}
   */
  async getStatusLocations(): Promise<any> {
    const RegisterStatus = Parse.Object.extend("RegisterStatus");
    const parseQuery = new Parse.Query(RegisterStatus);
    parseQuery.equalTo("BusinessID", this.business?.id);
    parseQuery.find().then((locationsStatuses: Array<LocationStatusType>) => {
      this.locationsStatuses = new LocationsStatuses(locationsStatuses);

      locationsStatuses.map((branch) => {
        this.branchesStatuses[branch.attributes.locationID] = branch;
      });
    });

    this.liveClientSubscribe = this.locationLiveStatus.subscribe(parseQuery);
    //update case
    this.liveClientSubscribe
      .on("update", async (locationDate) => {
        await this.updateOneLocationStatus(locationDate);
        this.key++;
      })

      //error case
      .on("error", (error) => {
        console.log("liveClientSubscribe ERROR", error);
      });
  }

  /**
   * Setup Channels Ides
   * @return {void}
   */
  async setLiveChannelsIdes() {
    this.channelsList = {};
    this.locationIds?.forEach((location) => {
      this.channelsList[location] = `${location}_call_center_${this.business.id}`;
    });
  }

  /**
   * Get Location Status by location id
   * @param locationId
   */
  getLocationStatus(locationId: string): LocationStatusInterface {
    return this.locationsStatuses?.list?.find((item) => item.locationId === locationId);
  }

  /**
   * Get Location Messages
   * @param locationId
   */
  getLocationMessages(locationId: string): any {
    return this.locationsStatuses?.list?.find((item) => item.locationId === locationId);
  }

  /***
   * Set all unread messages
   * @return {void}
   */
  async setNotificationMessages() {
    const query = new Parse.Query("chatMessage");
    const channels = this.locationIds?.map((location) => `${location}_call_center_${this.business.id}`);
    query.containedIn("Channel", channels);
    query.containedIn("Sender", this.locationIds);
    query.equalTo("isRead", false);
    query.limit(1000);

    if (!!this.locationLiveNotificationsSubscribe) {
      return;
    }

    this.locationLiveNotificationsSubscribe = this.locationLiveNotifications.subscribe(query);

    await query.find().then((notifications) => {
      notifications.forEach((notification) => {
        const branchesMessages = Object.assign({}, this.branchesMessages);

        if (branchesMessages[notification.attributes.Channel]) {
          branchesMessages[notification.attributes.Channel].count += 1;
        } else {
          branchesMessages[notification.attributes.Channel] = {
            messages: [notification],
            count: 1,
          };
        }
        this.branchesMessages = Object.assign({}, branchesMessages);
      });
    });

    this.locationLiveNotificationsSubscribe?.on("create", async (notification) => {
      const branchesMessages = Object.assign({}, this.branchesMessages);

      if (branchesMessages[notification.attributes.Channel]) {
        branchesMessages[notification.attributes.Channel].count += 1;
      } else {
        branchesMessages[notification.attributes.Channel] = {
          messages: [notification],
          count: 1,
        };
      }
      this.branchesMessages = Object.assign({}, branchesMessages);
    });
  }

  /**
   * Reset Branch un-read messages
   * @param channelID
   */
  async resetNotificationMessages(channelID = ""): Promise<void> {
    // reset the notification count
    const branchesMessages = Object.assign({}, this.branchesMessages);
    if (!branchesMessages[channelID]?.count) {
      return;
    }
    branchesMessages[channelID].count = 0;
    this.branchesMessages = Object.assign({}, branchesMessages);

    // submit reset
    const query = new Parse.Query(Parse.Object.extend("chatMessage"));
    query.equalTo("Destination", this.senderId);
    query.equalTo("Channel", channelID);
    query.equalTo("isRead", false);
    await query.find().then((messages) => {
      messages.forEach((message) => {
        message.set("isRead", true);
        message.save();
      });
    });
  }

  /**
   * Set Location messages
   * @param channelID
   * @param page
   */
  async setBranchMessages(channelID = "", page = 0) {
    const query = new Parse.Query(Parse.Object.extend("chatMessage"));
    query.equalTo("Channel", channelID);
    query.descending("createdAt");
    query.limit(20);
    query.skip(20 * page);
    const messages = await query.find();
    this.isBranchMessagesEmpty = window._.isEmpty(messages);

    this.setBranchMessagesData(channelID, messages || [], "desc");

    if (!this.liveChatSubscribe[channelID]) {
      this.liveChatSubscribe[channelID] = this.liveClient.subscribe(query).on("create", (message) => {
        //Checks if this message exists in old messages
        const isExists = !!this.branchesMessages[channelID]?.messages?.find((item) => item.id === message.id);
        if (!isExists) {
          this.setBranchMessagesData(channelID, [message]);
        }
      });
    }

    await this.setNotificationMessages();
  }

  /**
   * Set Branch Messages Data
   * @param channelId
   * @param messageList
   * @param sort
   */
  setBranchMessagesData(channelId: string, messageList: Array<any> = [], sort = "asc") {
    const branchesMessages = Object.assign({}, this.branchesMessages);

    const oldMessages = this.branchesMessages[channelId]?.messages || [];
    const messages = sort === "asc" ? [...messageList, ...oldMessages] : [...oldMessages, ...messageList];
    const count = messages.filter((i) => !i.attributes.isRead).length;

    branchesMessages[channelId] = {
      messages,
      count,
    };

    this.branchesMessages = Object.assign({}, branchesMessages);
  }

  /***
   * Send Branch Message
   * @param text
   * @param from
   * @param to
   * @param channelID
   */
  async sendBranchMessage(text = "", from: UserInterface, to = "", channelID = ""): Promise<any> {
    const chatMessage = Parse.Object.extend("chatMessage");
    const myNewObject = new chatMessage();
    myNewObject.set("isRead", false);
    myNewObject.set("CreationTime", new Date());
    myNewObject.set("Sender", this.senderId);
    // myNewObject.set("Sender", from.id);
    myNewObject.set("Channel", channelID);
    myNewObject.set("SentBy", `${from.first_name} ${from.last_name}`);
    myNewObject.set("Destination", to);
    myNewObject.set("Text", text);

    return myNewObject.save();
  }

  /**
   * Handle on Un Subscribe the LiveClient
   * @return {void}
   */
  unSubscribe(): void {
    this.liveClient?.unsubscribe();
    this.liveClient?.close();

    this.liveClient = null;
    this.liveChatSubscribe = {};
    this.chartMessageQuery = null;
    this.locationLiveNotificationsSubscribe = null;

    Parse.Object.destroyAll("RegisterStatus")?.then((peer) => {
      console.log(`Back4App Destroy ${peer}`);
    });

    Parse.Object.destroyAll("chatMessage")?.then((peer) => {
      console.log(`Back4App ChatMessage Destroy ${peer}`);
    });
  }
}

export default Back4App;

// @references:
// https://docs.parseplatform.org/js/guide/#live-queries
